import { useCallback } from 'react';
import { router, usePathname, type Href } from 'expo-router';
import { useAuthStore } from '../store/authStore';
import { useStructuresStore } from '../store/structuresStore';
import { useUIStore, type PendingProperty } from '../store/uiStore';

/**
 * Press handler for the Property tab (GlobalFloatingTabBar + SidebarNav) and
 * MapPathsOverlay's Property card. Resolves where a Property press should
 * land (resolvePropertyTarget) and acts on it: a straight href, the
 * property picker sheet when there's more than one way in, or the map's
 * address search when there's none. Same dismissTo('/') trip as
 * useOpenMapPathsOverlay.
 *
 * Reads auth and claims at press time via getState() on purpose: the tab
 * bars render on every screen, and subscribing them to the live area-claims
 * map would re-render both on every claim snapshot.
 */
/** All structure ids the signed-in user owns, preferring the fetched list
 *  (structuresStore.claimedStructureIds, populated by fetchClaimedStructures)
 *  over the live map's claim subscription (claimsByStructureId), which is
 *  only warm after the map has mounted and only covers the areas that have
 *  loaded. Falls back to the map when the fetch hasn't run this session. */
function getClaimedStructureIds(): string[] {
  const userId = useAuthStore.getState().user?.id;
  if (!userId) return [];
  const { claimedStructureIds, claimsByStructureId } = useStructuresStore.getState();
  if (claimedStructureIds.length > 0) return claimedStructureIds;
  return Object.keys(claimsByStructureId).filter(
    (id) => claimsByStructureId[id].claimedBy === userId
  );
}

/** The signed-in user's claimed structure id, if any, read off the same
 *  source resolvePropertyTarget uses. Also used by MapPathsOverlay's
 *  Property card. */
export function findClaimedStructureId(): string | undefined {
  return getClaimedStructureIds()[0];
}

function pendingHref(pending: PendingProperty): Href {
  return (
    pending.checklistId
      ? `/structure/${pending.structureId}?model=${encodeURIComponent(pending.checklistId)}`
      : `/structure/${pending.structureId}`
  ) as Href;
}

export type PropertyTarget = { kind: 'href'; href: Href } | { kind: 'picker' } | { kind: 'search' };

/**
 * Where a Property press should land, read at call time:
 *
 * - A pending insurer-link property (uiStore.pendingProperty) with no
 *   claims on file: straight to it (today's behaviour) — the link's whole
 *   point is "tap Property to see YOUR house". The model id rides along as
 *   `?model=` so the structure screen can preselect it.
 * - No pending link and no claims: `search` (fall back to the address bar).
 * - No pending link and exactly one claim: straight to it.
 * - Anything else — several claims, or a pending link alongside a claim —
 *   is ambiguous ("owner" vs. "asked to evaluate" for a different address)
 *   and goes to the picker instead of guessing.
 */
export function resolvePropertyTarget(): PropertyTarget {
  const pending = useUIStore.getState().pendingProperty;
  const claimedIds = getClaimedStructureIds();

  if (pending) {
    return claimedIds.length === 0
      ? { kind: 'href', href: pendingHref(pending) }
      : { kind: 'picker' };
  }
  if (claimedIds.length === 0) return { kind: 'search' };
  if (claimedIds.length === 1) return { kind: 'href', href: `/structure/${claimedIds[0]}` as Href };
  return { kind: 'picker' };
}

/**
 * Thin wrapper over resolvePropertyTarget for callers that only ever want a
 * direct link and are happy to treat everything else (search, or several
 * ways in) as null — the propertyJourney tests use it this way. New call
 * sites that need to handle the picker case should call
 * resolvePropertyTarget directly instead, the way useOpenProperty and
 * MapPathsOverlay's Property card do.
 */
export function resolvePropertyHref(): Href | null {
  const target = resolvePropertyTarget();
  return target.kind === 'href' ? target.href : null;
}

export function useOpenProperty() {
  const pathname = usePathname();
  const requestMapSearchFocus = useUIStore((s) => s.requestMapSearchFocus);

  return useCallback(() => {
    const target = resolvePropertyTarget();
    if (target.kind === 'href') {
      router.push(target.href);
      return;
    }
    if (target.kind === 'picker') {
      useUIStore.getState().openSheet('propertyPicker');
      return;
    }
    // push, not dismissTo — see the note in useOpenMapPathsOverlay:
    // dismissTo('/') no-ops when '/' isn't beneath the current route.
    if (pathname !== '/') router.push('/(tabs)' as Href);
    requestMapSearchFocus();
  }, [pathname, requestMapSearchFocus]);
}
